import type { SyncStatus } from '@awwyeah/shared';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type * as Y from 'yjs';
import { getDocManager } from './manager';

/**
 * The public sync API for module authors (docs/ARCHITECTURE.md § Sync layer).
 *
 * All of these are read-optimised: they subscribe to Yjs events and rerender. **Writes go
 * straight to the Yjs types** (`items.push([...])`, `map.set(id, value)`) — there is no
 * setter indirection, because Yjs is already the source of truth and a write is local,
 * instant and conflict-free.
 */

/** Opens `name` (IndexedDB + websocket) for as long as the component is mounted. */
export function useYDoc(name: string): Y.Doc {
  const manager = getDocManager();
  const doc = manager.doc(name);
  useEffect(() => {
    const handle = manager.acquire(name);
    return () => handle.release();
  }, [manager, name]);
  return doc;
}

/**
 * Resolves once `name`'s local (IndexedDB) load has completed — flips `false` again if
 * `name` changes. Offline-first means local history can already be sitting in IndexedDB
 * before any network sync happens, and it loads asynchronously; a caller that repaints
 * only in response to Yjs's observer events already gets this for free (applying the
 * loaded update fires the same observers as any other change), but a caller doing
 * imperative canvas work (SketchPad) wants an explicit, named trigger to schedule that
 * first paint against rather than relying entirely on the observer chain. Ref-counted via
 * the same `acquire`/`release` as `useYDoc` — safe to call alongside a sibling `useYDoc(name)`
 * for the same doc.
 */
export function useDocLocalLoaded(name: string): boolean {
  const manager = getDocManager();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
    let cancelled = false;
    const handle = manager.acquire(name);
    void handle.whenLocalLoaded.then(() => {
      if (!cancelled) setLoaded(true);
    });
    return () => {
      cancelled = true;
      handle.release();
    };
  }, [manager, name]);

  return loaded;
}

/** Connection-level sync status for the status indicator. */
export function useSyncStatus(): SyncStatus {
  const { status } = getDocManager();
  return useSyncExternalStore(status.subscribe, status.getStatus, status.getStatus);
}

/**
 * A live view of `doc.getMap(key)`. `entries` is a plain array recomputed on every change,
 * so it is safe to render and compare; `map` is the Yjs type you write to.
 */
export function useYMap<T>(doc: Y.Doc, key: string): { map: Y.Map<T>; entries: [string, T][] } {
  const map = doc.getMap<T>(key);
  const snapshot = useYSnapshot(map, () => [...map.entries()]);
  return { map, entries: snapshot };
}

/** A live view of `doc.getArray(key)`. */
export function useYArray<T>(doc: Y.Doc, key: string): { array: Y.Array<T>; items: T[] } {
  const array = doc.getArray<T>(key);
  const snapshot = useYSnapshot(array, () => array.toArray());
  return { array, items: snapshot };
}

/** One member's presence, as broadcast into a doc's awareness state (WS-16). */
export interface PresenceUser {
  userId: string;
  name: string;
  color: string;
}

/**
 * Who else has `docName` open right now, via the y-websocket awareness channel — ephemeral
 * per-connection state, never CRDT content. Publishes `me` into the local awareness state
 * and returns everyone *else's* state (deduped by `userId`, since one member can have the
 * doc open in more than one tab/device).
 *
 * Assumes `docName` is already kept open by a sibling `useYDoc(docName)` call in the same
 * component (or an ancestor) — this hook does not acquire/release the doc itself, it only
 * reads the provider's awareness once one exists, which is why it is safe to add without
 * touching `DocManager`'s doc-lifecycle contract.
 */
export function usePresence(docName: string, me: PresenceUser | null): PresenceUser[] {
  const manager = getDocManager();
  const [others, setOthers] = useState<PresenceUser[]>([]);

  useEffect(() => {
    if (!me) return undefined;
    const awareness = manager.awareness(docName);
    if (!awareness) return undefined;

    awareness.setLocalStateField('user', me);

    const read = (): void => {
      const seen = new Map<string, PresenceUser>();
      for (const state of awareness.getStates().values()) {
        const user = (state as { user?: PresenceUser }).user;
        if (user && user.userId !== me.userId) seen.set(user.userId, user);
      }
      setOthers([...seen.values()]);
    };
    awareness.on('change', read);
    read();
    return () => awareness.off('change', read);
  }, [manager, docName, me?.userId, me?.name, me?.color]);

  return others;
}

/** Any Yjs type can be watched deeply; structural typing avoids Y.AbstractType variance. */
type DeepObserver = Parameters<Y.Map<unknown>['observeDeep']>[0];

interface ObservableYType {
  observeDeep(handler: DeepObserver): void;
  unobserveDeep(handler: DeepObserver): void;
}

/**
 * Rerenders whenever `type` (or anything nested in it) changes, returning a cached
 * `read()` result. The cache is what makes this safe for `useSyncExternalStore`: the same
 * reference must come back until an actual change happens.
 */
export function useYSnapshot<S>(type: ObservableYType, read: () => S): S {
  const cache = useRef<{ type: ObservableYType; value: S; version: number } | null>(null);
  const version = useRef(0);
  const readRef = useRef(read);
  readRef.current = read;

  const subscribe = useCallback(
    (onChange: () => void) => {
      const handler = (): void => {
        version.current += 1;
        onChange();
      };
      type.observeDeep(handler);
      // Cover the render → subscribe gap: useSyncExternalStore subscribes in a passive
      // effect, after paint, so a change applied in between (IndexedDB hydration on a
      // slower device is the real case — Apps page, Dan's phone, 2026-08-12) fired no
      // handler and would otherwise be invisible until the *next* Yjs event, which for a
      // doc that is already fully synced never comes — the page sat on stale-empty until
      // a reload remounted it. Invalidating here makes React's own post-subscribe
      // consistency read recompute; one extra render per mount is the whole cost.
      version.current += 1;
      onChange();
      return () => type.unobserveDeep(handler);
    },
    [type],
  );

  const getSnapshot = useCallback((): S => {
    const cached = cache.current;
    if (!cached || cached.type !== type || cached.version !== version.current) {
      cache.current = { type, value: readRef.current(), version: version.current };
    }
    return (cache.current as { value: S }).value;
  }, [type]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
